import { X, AlertCircle, Megaphone, Info, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useFlashAnnouncements } from "@/hooks/useFlashAnnouncements";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

const FlashNewsBar = () => {
  const { announcements, dismissAnnouncement } = useFlashAnnouncements();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Rotate through multiple announcements
  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
      }, 30000); // Rotate every 30 seconds

      return () => clearInterval(interval);
    }
  }, [announcements.length]);

  const announcement = announcements[currentIndex];

  if (!announcement) return null;

  const getStyleConfig = (preset: string, type: string) => {
    if (preset === 'urgent' || type === 'breaking_news') {
      return {
        gradient: 'from-red-600 via-orange-600 to-red-600',
        icon: <AlertCircle className="w-5 h-5 animate-pulse" />,
        badge: 'BREAKING',
        badgeBg: 'bg-white/20',
      };
    }
    if (preset === 'marketing' || type === 'marketing') {
      return {
        gradient: 'from-blue-600 via-purple-600 to-blue-600',
        icon: <Megaphone className="w-5 h-5" />,
        badge: 'NEW',
        badgeBg: 'bg-white/20',
      };
    }
    return {
      gradient: 'from-slate-700 to-slate-600',
      icon: <Info className="w-5 h-5" />,
      badge: null,
      badgeBg: '',
    };
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date().getTime();
    const expiry = new Date(expiresAt).getTime();
    const diff = expiry - now;
    const minutes = Math.floor(diff / 60000);
    return minutes > 0 ? `${minutes}m` : 'Expiring soon';
  };

  const style = getStyleConfig(announcement.style_preset, announcement.type);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={announcement.id}
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className={`bg-gradient-to-r ${style.gradient} text-white overflow-hidden relative`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3 gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Icon */}
              <div className="flex-shrink-0 hidden sm:block">
                {style.icon}
              </div>

              {/* Badge */}
              {style.badge && (
                <span className={`${style.badgeBg} px-2 py-1 rounded text-xs font-bold flex-shrink-0`}>
                  {style.badge}
                </span>
              )}

              {/* Content */}
              <p className="text-sm sm:text-base font-medium flex-1 min-w-0 truncate sm:whitespace-normal">
                {announcement.content}
              </p>

              {/* Timer */}
              <span className="text-xs opacity-75 flex-shrink-0 hidden md:block">
                {getTimeRemaining(announcement.expires_at)}
              </span>

              {/* CTA Button */}
              {announcement.link_url && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-shrink-0 hidden sm:flex items-center gap-1"
                  onClick={() => window.open(announcement.link_url!, '_blank')}
                >
                  {announcement.link_text || 'Learn More'}
                  <ExternalLink className="w-3 h-3" />
                </Button>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => dismissAnnouncement(announcement.id)}
              className="flex-shrink-0 p-1 hover:bg-white/10 rounded transition-colors"
              aria-label="Dismiss announcement"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile CTA */}
          {announcement.link_url && (
            <div className="sm:hidden pb-3">
              <Button
                variant="secondary"
                size="sm"
                className="w-full flex items-center justify-center gap-1"
                onClick={() => window.open(announcement.link_url!, '_blank')}
              >
                {announcement.link_text || 'Learn More'}
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
          )}

          {/* Multiple announcements indicator */}
          {announcements.length > 1 && (
            <div className="flex justify-center gap-1 pb-2">
              {announcements.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentIndex ? 'bg-white w-4' : 'bg-white/40'
                  }`}
                  aria-label={`Go to announcement ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FlashNewsBar;
